import { SENTRY_KEYS } from '@ats/constants';
import AppLayout from '@layout/App';
import { AppStateProvider } from '@providers/AppStateProvider';
import { LoggerProvider } from '@providers/LoggerProvider';
import Setting from '@routes/setting';
import * as Sentry from '@sentry/react';
import { Provider } from 'jotai';
import { useEffect, type ReactElement } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';
import { isLocalEnv, sentryClientOptions } from './config/sentry.client.config';
import meetingConfig from './config';
import IntervieweeRoutes from './routes/interviewee';
import IntervieweeSecureRoutes from './routes/interviewee-secure';
import InterviewerRoutes from './routes/interviewer';
import MainRoutes from './routes/main';

if (!isLocalEnv) {
  Sentry.init({
    ...sentryClientOptions,
    integrations: [
      Sentry.reactRouterBrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],
  });
  Sentry.setTag(
    SENTRY_KEYS.TYPES.BROWSER,
    SENTRY_KEYS.SERVICES.VIDEO_INTERVIEW,
  );
}

export enum RouteType {
  MAIN,
  INTERVIEWER,
  INTERVIEWEE,
  INTERVIEWEE_SECURE,
  SETTING,
}

const RouteMap: Record<RouteType, ReactElement> = {
  [RouteType.MAIN]: <MainRoutes />,
  [RouteType.INTERVIEWER]: <InterviewerRoutes />,
  [RouteType.INTERVIEWEE]: <IntervieweeRoutes />,
  [RouteType.INTERVIEWEE_SECURE]: <IntervieweeSecureRoutes />,
  [RouteType.SETTING]: <Setting />,
};

const renderRoute = (type: RouteType) => {
  return RouteMap[type];
};

interface AppProps {
  type: RouteType;
}

function App({ type }: AppProps) {
  return (
    <Provider>
      <LoggerProvider logger={meetingConfig.logger}>
        <AppStateProvider>
          <AppLayout>{renderRoute(type)}</AppLayout>
        </AppStateProvider>
      </LoggerProvider>
    </Provider>
  );
}

export default App;
